import React, { useContext } from 'react';
import { AiChatServiceContext } from '../AiChatContext';
import { classifyPart, DEFAULT_PART_RENDERERS, type PartRendererProps } from './parts';

/**
 * Dispatcher: maps a part's event type to a renderer kind and forwards
 * props to that renderer. Per-kind components live in `./parts/`.
 *
 * The consumer override hook reads `partRenderers` off the AiChat services
 * context (set via `<AiChat partRenderers={...} />`). When no override is
 * provided for a given kind, the built-in `DEFAULT_PART_RENDERERS[kind]`
 * renders. Used outside an AiChatProvider, the dispatcher silently falls
 * back to the built-ins — `useContext` returns undefined rather than
 * throwing, so this stays usable for isolated rendering / tests.
 */
const MessagePart: React.FC<PartRendererProps> = React.memo((props) => {
    if(props.part.visible === false) return null;
    
    const services = useContext(AiChatServiceContext);
    const kind = classifyPart(props.part);
    if (!kind) return null;
    const Renderer = services?.partRenderers?.[kind] ?? DEFAULT_PART_RENDERERS[kind];
    return <Renderer {...props} />;
}, (prevProps, nextProps) => {
    // Comparator preserved verbatim from the pre-split MessagePart. Each
    // leaf only reads a narrow subset of these fields, but keeping the
    // union check here preserves the current re-render frequency
    // exactly. Narrower per-leaf memos can land as a follow-up.
    if (prevProps.part.id !== nextProps.part.id) return false;
    if ((prevProps.part.textDetails?.version ?? 0) < (nextProps.part.textDetails?.version ?? 0)) return false;
    if (prevProps.part.text !== nextProps.part.text) return false;
    if (prevProps.part.eventType !== nextProps.part.eventType) return false;
    if (prevProps.part.partial !== nextProps.part.partial) return false;
    if (prevProps.part.functionDetails?.text !== nextProps.part.functionDetails?.text) return false;
    if (prevProps.part.functionDetails?.args !== nextProps.part.functionDetails?.args) return false;
    if (prevProps.part.functionDetails?.response !== nextProps.part.functionDetails?.response) return false;
    if (prevProps.part.waitingDetails?.status !== nextProps.part.waitingDetails?.status) return false;
    if (prevProps.part.waitingDetails?.stopPoint !== nextProps.part.waitingDetails?.stopPoint) return false;
    if (prevProps.message.turn_complete !== nextProps.message.turn_complete) return false;
    if (prevProps.message.asyncFinalResponse !== nextProps.message.asyncFinalResponse) return false;
    if (prevProps.isLastInlineInGroup !== nextProps.isLastInlineInGroup) return false;
    if (prevProps.preview !== nextProps.preview) return false;
    if (prevProps.friendlyMode !== nextProps.friendlyMode) return false;

    return true;
});

export default MessagePart;
